<template>
  <VaCard>
    <VaCardTitle>
      <h3 class="mb-3 font-bold text-lg" style="text-transform: none">Servicios solicitados</h3>
    </VaCardTitle>
    <VaCardContent class="h-full scrollable-table">
      <table class="mt-4">
        <tbody>
          <template v-for="pos in showList" :key="pos.service.id + '-' + pos.operacion">
            <VaTimelineItem
              v-if="pos.operacion === 'solicitud'"
              :key="pos.service.id + '-solicitud'"
              :date="
                pos.eventDate.toLocaleDateString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              "
            >
              Se solicitó el servicio de <b>"{{ pos.service.motivo_solicitud }}"</b> para el
              <b>{{
                pos.eventDate.toLocaleDateString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              }}</b>
              con una embarcación de tipo <b>{{ pos.service.tipo_embarcacion }}</b
              >.
            </VaTimelineItem>
            <VaTimelineItem
              v-if="pos.operacion === 'respuesta'"
              :key="pos.service.id + '-respuesta'"
              :date="
                pos.eventDate.toLocaleDateString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              "
            >
              Se {{ pos.service.requestData?.estado == RequestStatus.APPROVED ? 'aprobó' : 'rechazó' }} el servicio de
              <b>"{{ pos.service.motivo_solicitud }}"</b> para el
              <b>{{
                pos.eventDate.toLocaleDateString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              }}</b>
              con una embarcación de tipo <b>{{ pos.service.tipo_embarcacion }}</b
              >.
            </VaTimelineItem>
            <VaTimelineItem
              v-if="pos.operacion === 'tarifa_especial'"
              :key="pos.service.id + '-tarifa_especial'"
              :date="
                pos.eventDate.toLocaleDateString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              "
            >
              Se aprobó una tarifa especial para la embarcación de tipo
              <b>{{ pos.service.tipo_embarcacion }}</b> en el servicio de <b>"{{ pos.service.motivo_solicitud }}"</b>.
            </VaTimelineItem>
            <VaTimelineItem
              v-if="pos.operacion === 'posicionamiento'"
              :key="pos.service.id + '-posicionamiento'"
              :date="
                pos.eventDate.toLocaleDateString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              "
            >
              La embarcación de tipo <b>{{ pos.service.tipo_embarcacion }}</b> fue posicionada para el servicio de
              <b>"{{ pos.service.motivo_solicitud }}"</b>.
            </VaTimelineItem>
          </template>
        </tbody>
      </table>
    </VaCardContent>
  </VaCard>
</template>
<script setup lang="ts">
import { onMounted, ref, Ref, watch } from 'vue'
import VaTimelineItem from '../../../../components/va-timeline-item.vue'
import dataStorageManager, { BucketDefaultIDs } from '../../../../managers/data/DataStorageManager'
import { useComplexBucketList } from '../../../../managers/data/utilities/complexBucketListController'
import { IRequest, RequestObjectType, RequestStatus } from '../../../../services/api-services/RequestsServices'
import { ITripulante } from '../../../../services/api-services/TripulantesServices'
import {
  IViaje,
  OperatedViaje,
  requestedTripulante,
  ViajeCancelado,
  ViajeEstado,
} from '../../../../services/api-services/ViajesServices'
import { IEmbarcacion } from '../../../../services/api-services/EmbarcacionesServices'
import { v4 as uuid } from 'uuid'
import identityManager, { LoggedInUser } from '../../../../managers/identity/IdentityManager'
import { IArmador } from '../../../../services/api-services/ArmadoresServices'
import { IService, OperatedService } from '../../../../services/api-services/ServicesServices'
import { IServiceVehicle } from '../../../../services/api-services/ServiceVehiclesServices'
import { IProveedor } from '../../../../services/api-services/ProveedoresServices'

const userData: Ref<LoggedInUser | null> = ref(null)
const proveedorData: Ref<IProveedor | null> = ref(null)

const servicesList: Ref<Array<IService>> = ref([])
const fullServicesList: Ref<Array<OperatedService>> = ref([])

const vehiculosServicios: Ref<IServiceVehicle[]> = ref([])

const requestsList: Ref<IRequest[]> = ref([])
const vehiclesRequests: Ref<IRequest[]> = ref([])
const tarifasEspecialesRequests: Ref<IRequest[]> = ref([])
const posicionamientosRequests: Ref<IRequest[]> = ref([])

const fullList: Ref<
  {
    service: OperatedService
    operacion: 'solicitud' | 'respuesta' | 'tarifa_especial' | 'posicionamiento'
    eventDate: Date
    tarifaEspecial: IRequest | undefined
  }[]
> = ref([])
const showList: Ref<
  {
    service: OperatedService
    operacion: 'solicitud' | 'respuesta' | 'tarifa_especial' | 'posicionamiento'
    eventDate: Date
    tarifaEspecial: IRequest | undefined
  }[]
> = ref([])

onMounted(() => {
  userData.value = identityManager.getUser()
  if (userData.value) {
    const currentProveedor: IProveedor | undefined = dataStorageManager.searchDataInBucket<IProveedor>(
      BucketDefaultIDs.PROVEEDORES,
      (p) => p.rut_representante.toString() === userData.value!.rut.toString(),
    )
    if (currentProveedor) {
      proveedorData.value = currentProveedor
    }
  }

  dataStorageManager.subscribeBucket(BucketDefaultIDs.SERVICES, (services: IService[]) => {
    servicesList.value = services
    loadFullServices()
  })
  dataStorageManager.subscribeBucket<IServiceVehicle>(BucketDefaultIDs.VEHICLE_SERVICES, (vehiculos) => {
    vehiculosServicios.value = vehiculos
    loadFullServices()
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, onRequestsLoad)
})

watch(fullServicesList, () => {
  fullList.value = fullServicesList.value
    .map((service) => {
      const operaciones = []
      if (service.requestData) {
        operaciones.push({
          service: service,
          operacion: 'solicitud' as 'solicitud' | 'respuesta',
          eventDate: new Date(service.requestData.fecha_solicitud),
          tarifaEspecial: undefined,
        })
        if (
          service.requestData.estado === RequestStatus.APPROVED ||
          service.requestData.estado === RequestStatus.REJECTED
        ) {
          operaciones.push({
            service: service,
            operacion: 'respuesta' as 'solicitud' | 'respuesta',
            eventDate: new Date(service.requestData.fecha_respuesta!),
            tarifaEspecial: undefined,
          })
        }
        if (service.tarifasEspecialesData.length > 0) {
          service.tarifasEspecialesData.forEach((tarifa) => {
            operaciones.push({
              service: service,
              operacion: 'tarifa_especial' as const,
              eventDate: new Date(tarifa.fecha_solicitud),
              tarifaEspecial: tarifa,
            })
          })
        }
        if (service.posicionamientosData.length > 0) {
          service.posicionamientosData.forEach((posicionamiento) => {
            operaciones.push({
              service: service,
              operacion: 'posicionamiento' as const,
              eventDate: new Date(posicionamiento.fecha_solicitud),
              tarifaEspecial: undefined,
            })
          })
        }
      }
      return operaciones
    })
    .flat()
    .filter((p) => p !== undefined) as {
    service: OperatedService
    operacion: 'solicitud' | 'respuesta' | 'tarifa_especial' | 'posicionamiento'
    eventDate: Date
    tarifaEspecial: IRequest | undefined
  }[]
})

watch(fullList, () => {
  const less12 = new Date(new Date().getTime() - 10 * 1000 * 60 * 60)
  const plus12 = new Date(new Date().getTime() + 24 * 1000 * 60 * 60)
  showList.value = fullList.value
    .sort((a, b) => a.eventDate.getTime() - b.eventDate.getTime())
    // Cortar a los 15 últimos eventos. Si los eventos estan entre hoy -10h y hoy +24h, mostrarlos
    .filter((v, index) => {
      const eventDate = new Date(v.service.fecha_inicio)
      return (eventDate.getTime() > less12.getTime() && eventDate.getTime() < plus12.getTime()) || index < 15
    })
    .sort((a, b) => b.eventDate.getTime() - a.eventDate.getTime())
})

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Servicio)
  tarifasEspecialesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.TarifaEspecial)
  posicionamientosRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PosicionamientoServicio)
  vehiclesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.VehiculoServicio)
  loadFullServices()
}

function loadFullServices() {
  if (servicesList.value.length > 0 && requestsList.value.length > 0) {
    fullServicesList.value = servicesList.value
      .filter((item) => item.tipo_servicio === 'proveedor' && item.usuario_registro === proveedorData.value?.id)
      .map((item) => {
        const request: IRequest | undefined = requestsList.value
          .filter((req) => req.tipo_objeto === RequestObjectType.Servicio && req.tipo_objeto_2 === null)
          .filter((req) => req.id_objeto.toString() === item.id.toString())
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
        const posicionamientosRequestsData: IRequest[] = posicionamientosRequests.value.filter(
          (req) => req.id_objeto.toString() === item.id.toString(),
        )
        const tarifasEspecialesRequestsData: IRequest[] = tarifasEspecialesRequests.value.filter(
          (req) => req.id_objeto.toString() === item.id.toString(),
        )

        const vehiculosRequests: IRequest[] = vehiclesRequests.value.filter(
          (req) => req.id_objeto.toString() === item.id.toString(),
        )
        const vehiculosServicio: IServiceVehicle[] = vehiculosRequests
          .map((req) => {
            return vehiculosServicios.value.find(
              (vehiculo) => req.id_objeto_2 !== null && vehiculo.id.toString() === req.id_objeto_2.toString(),
            )
          })
          .filter((vehiculo): vehiculo is IServiceVehicle => vehiculo !== undefined)

        const usuarioSolicitante: IProveedor = proveedorData.value!

        return {
          ...item,
          requestData: request,
          posicionamientosData: posicionamientosRequestsData,
          tarifasEspecialesData: tarifasEspecialesRequestsData,
          vehiculosData: vehiculosServicio,
          vehiculosRequestsData: vehiculosRequests,
          usuarioSolicitanteData: usuarioSolicitante,
          embarcacionData: undefined,
        }
      })
  }
}
</script>
<style scoped>
.limited-height-card {
  max-height: 400px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scrollable-table {
  overflow-y: auto;
  height: 100%;
  max-height: 520px;
}

.content-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

b {
  color: #4774bd; /* Tailwind's blue-500 color */
}
</style>
